import React, { useContext, useEffect, useState } from "react";
import NavBar from "../../components/NavBar";
import SearchHashtags from "../../components/search/SearchHashtags";
import { useLocation } from "react-router-dom";
import { Filter } from "./Filter";
import FeedAdvertisement from "../../components/feeds/sidebar/FeedAdvertisement";
import { getAllSearchAgainstKeywordApi } from "../../services/searchApis";
import { useInfiniteQuery } from "react-query";
import InfiniteScroll from "react-infinite-scroller";
import { toast } from "react-toastify";
import { followUserApi } from "../../services/feedApis";
import { Language, LoginUserData } from "../../App";
import { useSplashAds } from "../../hooks/useSplashAds";
import SearchResultCard from "../../components/search/SearchResultCard";
import { DoctorCard } from "../../components/search/DoctorsResultsComp";
import { LuStethoscope, LuUserX, LuSearch } from "react-icons/lu";
import "./SearchPage.css";

export default function DoctorsSearch() {
  const query = useLocation().search;
  const { lang } = useContext(Language);

  const keyword = decodeURIComponent(query.split("=")[1] || "");
  const { splashData } = useContext(LoginUserData);
  const { ads: splashAds, isReady: areSplashAdsReady } = useSplashAds();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCities, setSelectedCities] = useState(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const [selectedSpecializations, setSelectedSpecializations] = useState([]);

  const country = selectedCountry;
  const city = selectedCities;
  const specialization = selectedSpecializations;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useInfiniteQuery({
      queryKey: ["search-doctors", keyword],
      queryFn: ({ pageParam = 1 }) =>
        getAllSearchAgainstKeywordApi({
          keyword,
          type: "doctors",
          pageParam,
          country,
          city,
          specialization,
        }),
      enabled: !!keyword,
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.data.current_page + 1;
        return nextPage <= lastPage.data.last_page ? nextPage : undefined;
      },
      onError: (error) => {
        toast.error(error.message);
      },
      refetchOnWindowFocus: false,
    });

  useEffect(() => {
    setIsRefetching(true);
    refetch().finally(() => setIsRefetching(false));
  }, [selectedCountry, selectedCities, selectedSpecializations, refetch]);

  const doctors = data?.pages?.flatMap((page) => page?.data?.data) || [];
  const leftAds = splashAds?.search_left || [];
  const rightAds = splashAds?.search_right || [];
  const hashTags = data?.pages?.flatMap((page) => page?.hash_tags);
  const countries = JSON.parse(localStorage.getItem("countriesData")) || [];
  const storedValue = JSON.parse(localStorage.getItem("contest_obj"));

  return (
    <div className="search-page-wrapper">
      <NavBar />
      <Filter
        title={lang?.doctors || "Doctors"}
        options={[
          {
            title: lang?.specialization || "Specialization",
            options: splashData?.home?.subcategories,
          },
          { title: lang?.country || "Country", options: countries },
        ]}
        onCountrySelect={(countryId) =>
          setSelectedCountry(countryId ? countryId.map((spec) => spec.value) : [])
        }
        onCitySelect={(id) => setSelectedCities(id || null)}
        onSpecializationSelect={(specializations) =>
          setSelectedSpecializations(specializations ? specializations.map((spec) => spec.label) : [])
        }
      />

      <div className="search-layout-container">
        {/* Left Column: Ads */}
        <div className="search-left-col">
          <FeedAdvertisement
            ads={leftAds}
            loading={!areSplashAdsReady}
            new_contest_obj={storedValue}
          />
        </div>

        {/* Center Column: Results */}
        <div className="search-main-col">
          <div className="search-results-header-inline">
            <h1 className="search-main-heading">
              <span>{lang?.doctors || "Doctors"}</span>
              {keyword && (
                <span className="search-keyword-badge">
                  <LuSearch size={14} />
                  <span>"{keyword}"</span>
                </span>
              )}
            </h1>
            {!isLoading && !isRefetching && doctors?.length > 0 && (
              <span className="search-results-summary">
                {doctors.length} {doctors.length === 1 ? (lang?.result || "result") : (lang?.results || "results")} {lang?.found || "found"}
              </span>
            )}
          </div>

          <DoctorsList
            loading={isLoading || isRefetching}
            data={doctors}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        </div>

        {/* Right Column: Hashtags & Ads */}
        <div className="search-right-col">
          <SearchHashtags data={hashTags} />
          <FeedAdvertisement
            ads={rightAds}
            loading={!areSplashAdsReady}
            new_contest_obj={storedValue}
          />
        </div>
      </div>
    </div>
  );
}

const DoctorsList = ({
  data,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  loading,
}) => {
  const { lang } = useContext(Language);
  const [searchData, setSearchData] = useState(data || []);

  const handleFollow = async (id, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const isFollowed = searchData?.find((item) => item?.id === id)?.follow;
    try {
      const response = await followUserApi(id);
      if (response.status) {
        toast.success(response.message);
        const updatedData = searchData?.map((item) => {
          if (item?.id === id) {
            return { ...item, follow: !isFollowed, is_following: !isFollowed };
          }
          return item;
        });
        setSearchData(updatedData);
      } else {
        toast.error(response.error);
      }
    } catch (error) {
      toast.error(error?.message);
    }
  };

  useEffect(() => {
    setSearchData(data || []);
  }, [data]);

  return (
    <SearchResultCard
      icon={<LuStethoscope />}
      title={lang?.doctors || "Doctors"}
      count={searchData?.length}
      seeAll={false}
      showHeader={false}
    >
      <InfiniteScroll
        pageStart={0}
        loadMore={() => {
          if (!isFetchingNextPage) {
            fetchNextPage();
          }
        }}
        hasMore={hasNextPage}
        loader={
          <div className="search-doctors-list mt-3">
            {[1, 2].map((n) => (
              <div key={n} className="search-doctor-card">
                <div className="search-doctor-profile-link">
                  <div
                    className="search-doctor-avatar-wrap search-shimmer-box"
                  />
                  <div className="search-doctor-info" style={{ width: "60%" }}>
                    <div className="search-shimmer-box mb-2" style={{ height: "15px", width: "70%" }} />
                    <div className="search-shimmer-box" style={{ height: "12px", width: "45%" }} />
                  </div>
                </div>
                <div className="search-shimmer-box" style={{ height: "32px", width: "80px", borderRadius: "999px" }} />
              </div>
            ))}
          </div>
        }
      >
        {loading ? (
          <div className="search-doctors-list">
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} className="search-doctor-card">
                <div className="search-doctor-profile-link">
                  <div
                    className="search-doctor-avatar-wrap search-shimmer-box"
                  />
                  <div className="search-doctor-info" style={{ width: "60%" }}>
                    <div className="search-shimmer-box mb-2" style={{ height: "15px", width: "70%" }} />
                    <div className="search-shimmer-box" style={{ height: "12px", width: "45%" }} />
                  </div>
                </div>
                <div className="search-shimmer-box" style={{ height: "32px", width: "80px", borderRadius: "999px" }} />
              </div>
            ))}
          </div>
        ) : searchData && searchData.length > 0 ? (
          <div className="search-doctors-list">
            {searchData.map((item, key) => (
              <DoctorCard
                key={item?.id || key}
                item={item}
                onFollow={handleFollow}
              />
            ))}
          </div>
        ) : (
          <div className="search-empty-state-card">
            <div className="search-empty-icon-wrap">
              <LuUserX />
            </div>
            <h5 className="search-empty-title">{lang?.no_doctors_found || "No doctors found"}</h5>
            <p className="search-empty-desc">
              {lang?.try_different_keyword || "Try a different keyword or check spelling."}
            </p>
          </div>
        )}
      </InfiniteScroll>
    </SearchResultCard>
  );
};
